import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Text, Flex, useToast, Skeleton, Switch, Icon } from '@chakra-ui/react';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { ShortcutCard } from '../Home/components/ShortcutCard';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ReactComponent as LinkIcon } from '@/assets/icons/link-icon.svg';
import { breadcrumb } from './breadcrumb';
// import { ReactComponent as FeedIcon } from '@/assets/icons/feed-icon.svg';
// import { ReactComponent as InfluencerIcon } from '@/assets/icons/influencer-icon.svg';
// import { ReactComponent as ViralIcon } from '@/assets/icons/viral-icon.svg';

type Link = {
  label: string;
  url: string;
  icon: ReactNode;
};

export const ApplicationSocialMedia = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const links = [
    { label: 'Canais Oficiais', url: '/application/social-media/links', icon: <Icon as={LinkIcon} /> },
    // { label: 'Feed', url: '/application/social-media/feed', icon: <Icon as={FeedIcon} /> },
    // { label: 'Influencers', url: '', icon: <Icon as={InfluencerIcon} /> },
    // { label: 'Post Virais', url: '/application/social-media/viral-posts', icon: <Icon as={ViralIcon} /> },
  ];

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    undefined,
    ApplicationComponentId.SOCIAL_MEDIA,
  );

  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successKey = pendingAppAreaState ? 'area_toggled_enable' : 'area_toggled_disable';
    const errorKey = pendingAppAreaState ? 'area_toggle_failed_enable' : 'area_toggle_failed_disable';

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation(
      [
        { component_id: ApplicationComponentId.SOCIAL_MEDIA_LINKS, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.SOCIAL_MEDIA_FEED, active: pendingAppAreaState },
        // { component_id: ApplicationComponentId.SOCIAL_MEDIA_INFLUENCERS, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.SOCIAL_MEDIA_VIRAL_POSTS, active: pendingAppAreaState },
      ],
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t(`pages.application_social_media.social_media_${successKey}`),
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: t(`pages.application_social_media.social_media_${errorKey}`),
          });

          setIsAppAreaActive(!pendingAppAreaState);
        },
      },
    );

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t(`pages.application_social_media.social_media_title`)} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t(`pages.application_social_media.social_media`)}
              </Text>

              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive ? t(`pages.application_social_media.social_media_enabled`) : t(`pages.application_social_media.social_media_disabled`)}
              </Switch>
            </>
          )}
        </Flex>

        <Flex gap='xxsmall' flexWrap='wrap'>
          {links?.map(link => (
            <Link key={link.url} to={link.url}>
              <ShortcutCard label={link.label} icon={link.icon} />
            </Link>
          ))}
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t(`pages.application_social_media.social_media`)}
        description={
          <Text>
            {pendingAppAreaState
              ? t(`pages.application_social_media.social_media_description_enabled`)
              : t(`pages.application_social_media.social_media_description_disabled`)}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
